// AttendeesDrawer.jsx
import React, { useMemo, useState } from 'react';
import {
  Drawer,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import {
  MdArrowBackIos,
  MdSearch,
  MdClose,
} from 'react-icons/md';
import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import user from 'assets/img/icons/user.png';

export default function AttendeesDrawer({
  open,
  onClose,
  attendees = [],       // flat array of attendee objects (recommended + other)
  onSelectAttendee,     // (attendeeItem) => void
  title = 'Guests',
}) {
  const [query, setQuery] = useState('');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const drawerWidth = isMobile ? '100%' : '50%';
  // Normalize + filter by search query
  const filteredAttendees = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return attendees;
    return attendees.filter((item) => {
      const first = item?.user?.firstName?.toLowerCase() || '';
      const last = item?.user?.lastName?.toLowerCase() || '';
      const full = `${first} ${last}`.trim();
      return full.includes(q) || first.includes(q) || last.includes(q);
    });
  }, [attendees, query]);

  const getInitials = (firstName, lastName) => {
    const a = (firstName || '').trim();
    const b = (lastName || '').trim();
    if (a && b) return `${a[0]}${b[0]}`.toUpperCase();
    if (a) return a.slice(0, 2).toUpperCase();
    return '?';
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = user;
  };

  const renderAvatar = (attendeeItem) => {
    const photoUrl = attendeeItem?.user?.photoUrl;
    if (photoUrl) {
      return (
        <img
          src={photoUrl}
          alt={attendeeItem?.user?.firstName || 'attendee'}
          className="h-12 w-12 rounded-full object-cover"
          onError={handleImageError}
        />
      );
    }
    return (
      <div className="h-12 w-12 rounded-full bg-gray-100 text-gray-500 font-semibold text-sm flex items-center justify-center">
        {getInitials(attendeeItem?.user?.firstName, attendeeItem?.user?.lastName)}
      </div>
    );
  };
 const buildInstagramUrl = (v) => {
  if (!v) return null;
  const val = String(v).trim();
  if (/^https?:\/\//i.test(val)) return val;                     // already full
  // Some backends return "instagram.com/xxx" without protocol
  if (/^www\./i.test(val) || /^instagram\.com/i.test(val)) {
    return `https://${val.replace(/^https?:\/\//i, '')}`;
  }
  return `https://www.instagram.com/${val.replace(/^@/, '')}`;
};

 const buildLinkedInUrl = (v) => {
  if (!v) return null;
  const val = String(v).trim();
  if (/^https?:\/\//i.test(val)) return val;                     // already full
  if (/^www\./i.test(val) || /^linkedin\.com/i.test(val)) {
    return `https://${val.replace(/^https?:\/\//i, '')}`;
  }
  // If it already starts with in/ or company/, just prefix domain
  if (/^(in|company)\//i.test(val)) {
    return `https://www.linkedin.com/${val.replace(/^@/, '')}`;
  }
  // Bare slug → default to /in/
  return `https://www.linkedin.com/in/${val.replace(/^@/, '')}`;
};

const handleInstagramClick = (e, url) => {
  e.stopPropagation();
  const full = buildInstagramUrl(url);
  if (full) window.open(full, '_blank', 'noopener,noreferrer');
};

const handleLinkedInClick = (e, url) => {
  e.stopPropagation();
  const full = buildLinkedInUrl(url);
  if (full) window.open(full, '_blank', 'noopener,noreferrer');
};

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: drawerWidth,
          backgroundColor: '#F8F9FB',
        },
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-14">
          <IconButton onClick={onClose} aria-label="Back" size="small">
            <MdArrowBackIos className="text-navy-700" size={18} />
          </IconButton>

          <h2 className="text-base font-semibold text-navy-700">
            {filteredAttendees.length} {title}
          </h2>

          <IconButton aria-label="Search" size="small">
            <MdSearch className="text-navy-700" size={22} />
          </IconButton>
        </div>

        {/* Search input */}
        <div className="px-4 pb-3">
          <TextField
            fullWidth
            size="small"
            placeholder="Search guests..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch className="text-gray-400" size={18} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQuery('')}>
                    <MdClose size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: '10px',
                backgroundColor: '#F1F5F9',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto hide-scrollbar bg-[#F8F9FB]">
        {filteredAttendees.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">
            No guests found.
          </div>
        ) : (
          filteredAttendees.map((attendeeItem, idx) => {
            const instagram = attendeeItem?.user?.instagram  || null;
            const linkedin = attendeeItem?.user?.linkedIn  || null;

            return (
              <div
                key={attendeeItem?.id || idx}
                onClick={() => onSelectAttendee?.(attendeeItem)}
                className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition"
              >
                {/* Avatar */}
                <div className="flex-shrink-0">{renderAvatar(attendeeItem)}</div>

                {/* Name + subtitle */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-[15px] font-semibold text-navy-700 truncate">
                    {attendeeItem?.user?.firstName
                      ? `${attendeeItem.user.firstName}${
                          attendeeItem.user.lastName
                            ? ' ' + attendeeItem.user.lastName
                            : ''
                        }`
                      : 'Anonymous'}
                  </h4>
                  {attendeeItem?.user?.headline ||
                  attendeeItem?.user?.bio ? (
                    <p className="text-xs text-gray-500 truncate">
                      {attendeeItem.user.headline || attendeeItem.user.bio}
                    </p>
                  ) : null}
                </div>

                {/* Social icons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {instagram && (
                    <button
                      type="button"
                      onClick={(e) => handleInstagramClick(e, instagram)}
                      aria-label="Instagram profile"
                      className="text-gray-400 hover:text-pink-500 transition"
                    >
                      <FaInstagram size={26} />
                    </button>
                  )}
                  {linkedin && (
                    <button
                      type="button"
                      onClick={(e) => handleLinkedInClick(e, linkedin)}
                      aria-label="LinkedIn profile"
                      className="text-gray-400 hover:text-blue-600 transition"
                    >
                      <FaLinkedin size={26} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Drawer>
  );
}